import { useState } from "react";
import { Reveal } from "@/components/Reveal";

const DEC = [
  ["Station-level budget structure", "Station \u00d7 account code for all markets. Smaller markets can use an \u201cAll stations\u201d line per account code \u2014 the roll-up handles both", "mmg", 1],
  ["Account code mapping", "We draft it from the templates and the existing WideOrbit categorization workflow. MMG confirms", "mmg", 1],
  ["Paid Programming source of truth", "The Paids tab is the only source, removed from AE tabs unless an AE genuinely owns it", "mmg", 1],
  ["Calendar months", "Follow WideOrbit's billing month (likely the broadcast calendar), confirmed with a sample export", "mmg", 1],
  ["Owner mapping", "We prepare a sheet from WideOrbit AE codes, HubSpot users and the 2026 file. MMG fills the gaps", "mmg", 1],
  ["Future WideOrbit billing", "LeadG2 pulls a sample to confirm whether future booked months sync", "leadg2", 1],
  ["Closed Won handoff", "Capture the WideOrbit order ID on the deal, and remove forecast once booked revenue appears. Fallback: hold forecast for 30 days after close", "both", 1],
  ["Please list all 13 markets MMG operates in", "So we can confirm which one isn't in the current budget file", "mmg", 0],
  ["Real station names behind Properties 3-14, especially the four sharing code 22", "Stations must be told apart", "mmg", 0],
  ["Are LSM and NSM budgets the managers' own targets, or house accounts?", "Affects AE vs market dashboards", "mmg", 0],
  ["Can we use the June-Dec 2026 budget as sandbox test data?", "Lets us build before 2027 figures arrive", "mmg", 0],
  ["Which billing codes don't match the budget account codes today?", "Booked revenue must line up with budget", "leadg2", 0],
  ["Who adds line items to deals already open in the pipeline?", "We'd suggest the AEs, using our template", "mmg", 0],
  ["Does MMG need markets restricted from viewing each other's data?", "Filtering by market is included; restriction is quoted separately if needed", "mmg", 0],
];

const OWNER = { mmg: "MMG", leadg2: "LeadG2", both: "MMG + LeadG2" };
const PILL = {
  mmg: "bg-coal text-white",
  leadg2: "border border-hair text-fog bg-white",
  both: "bg-sunk text-fog",
};

export default function Decisions() {
  const [owner, setOwner] = useState("all");
  const rows = DEC.filter((d) => owner === "all" || d[2] === owner || d[2] === "both");

  return (
    <section id="decisions" className="mx-auto max-w-6xl px-4 sm:px-6 mt-14 scroll-mt-16" data-testid="section-decisions">
      <Reveal>
        <p className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-fog">Open decisions</p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-coal">
          What we'd like to settle before development starts
        </h2>
        <p className="mt-2 text-[15px] text-fog max-w-3xl leading-relaxed">
          Seven decisions affect the build. Before the working session, we'll send MMG a
          short input form covering all of these questions, with our recommended default
          for each. Answering them ahead of time means the session can focus on whatever
          still needs discussion. After it, MMG signs off a decisions log within 2
          business days, and anything still open falls back to our default.
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-5 bg-white border border-hair rounded-md p-5" data-testid="decisions-panel">
          <div className="inline-flex border border-hair rounded overflow-hidden bg-white" role="group" aria-label="Filter by owner" data-testid="decisions-owner-filters">
            {[
              ["all", "All"],
              ["mmg", "MMG"],
              ["leadg2", "LeadG2"],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-testid={`decisions-owner-${k}`}
                onClick={() => setOwner(k)}
                className={`px-3 py-1.5 font-mono text-[12px] transition-colors duration-200 ${owner === k ? "bg-coal text-white" : "text-fog hover:text-coal"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-3">
            {rows.map((d) => {
              const i = DEC.indexOf(d);
              return (
                <div key={i} className="grid grid-cols-[28px_1fr_auto] gap-2.5 py-2.5 border-b border-hair last:border-0 items-start" data-testid={`decision-row-${i + 1}`}>
                  <span className="font-mono text-[12px] text-fog pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="text-[14px] text-coal leading-relaxed">
                      {d[0]}
                      {d[3] === 1 && (
                        <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.08em] bg-sunk text-fog rounded px-1.5 py-0.5 align-middle" data-testid={`decision-default-badge-${i + 1}`}>
                          default proposed
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-fog">{d[1]}</p>
                  </div>
                  <span className={`font-mono text-[11px] rounded px-2 py-0.5 whitespace-nowrap ${PILL[d[2]]}`}>{OWNER[d[2]]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
